import { PlanCode, SourceType, type PlanEntitlements } from './enums';

/** One line of a plan card on the public site: a bold label and its value. */
export interface PlanCardRow {
  label: string;
  value: string;
  /** Drawn as a cross, greyed: the plan does not have it. */
  off: boolean;
}

// Not in the entitlements, so these two are the card's own copy until a manager writes theirs.
const MANUAL: Partial<Record<PlanCode, string>> = {
  [PlanCode.ESSENTIAL]: '10',
  [PlanCode.PRO]: '100',
  [PlanCode.PREMIUM]: 'Unlimited',
};

/** The card as it reads when no one has written it: what the plan includes is read from `ent`. */
export function defaultCardRows(code: PlanCode, ent: PlanEntitlements, leakSites: string): PlanCardRow[] {
  const social = ent.sources.includes(SourceType.SOCIAL);
  const rows: [string, string][] = [
    ['Scan frequency', ent.cardScanFrequency],
    ['Platforms', social ? 'Google, leak websites, Bing, Yahoo' : `Google, leak websites (${leakSites} websites)`],
    ['Social platforms', social ? 'Yes' : 'No'],
    ['Stage name', String(ent.usernamesIncluded)],
    ['Manual submissions', MANUAL[code] ?? 'Unlimited'],
    ['Support', ent.priority > 0 ? 'Dedicated manager' : 'Chat & email support'],
  ];
  return rows.map(([label, value]) => ({ label, value, off: value === 'No' }));
}

/**
 * The card a manager wrote: one `Label: value` per line. A line without a colon
 * is skipped and a value of "No" draws the row crossed out. Blank text is not a
 * card, so the caller falls back to the default.
 */
export function parseCardRows(text: string | null | undefined): PlanCardRow[] | null {
  const rows = (text ?? '')
    .split('\n')
    .map((line) => {
      const at = line.indexOf(':');
      if (at < 1) return null;
      const label = line.slice(0, at).trim();
      const value = line.slice(at + 1).trim();
      return label && value ? { label, value, off: value.toLowerCase() === 'no' } : null;
    })
    .filter((row): row is PlanCardRow => row !== null);
  return rows.length ? rows : null;
}
